import StatusTag from "common/Tags/StatusTag.tsx";
import React from "react";
import {
  AssetManagerInvoice,
  AssetManagerInvoiceStatus,
  LanguageNameSpaces,
} from "@shared/types";
import { useTheme } from "styled-components";
import { sharedLib } from "@shared/services/shared.service.ts";
import { displayResultMessage } from "utils/misc.tsx";
import useDownloadFile from "Global/useDownloadFile.ts";
import { INVOICE_STATUS_TO_DISPLAY } from "features/payouts/Invoices/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { MenuItemType } from "antd/es/menu/interface";
import LookIcon from "common/Icons/LookIcon.tsx";
import {
  Check,
  Download,
  Eye,
  EyeOff,
  RefreshCcw,
  Trash2,
  X,
} from "lucide-react";
import StatusDot from "features/admin/NewLook/StatusDot";
import { getNewLookTokens } from "common/NewLook/tokens.ts";

export interface InvoiceAction extends MenuItemType {
  key: string;
  icon: React.JSX.Element;
  label: string;
  onClick: () => void;
}

const useInvoice = () => {
  const theme = useTheme();
  const { downloadFile } = useDownloadFile();
  const { t } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const { isSubUser } = useSubUserPermissions();

  const [updateStatus] =
    sharedLib.sharedApi.settings.useUpdateAssetManagerInvoiceStatusMutation();
  const [updateVisibility] =
    sharedLib.sharedApi.settings.useUpdateAssetManagerInvoiceVisibilityMutation();

  const updateInvoiceStatus = (
    invoiceId: number,
    status: AssetManagerInvoiceStatus
  ) => {
    displayResultMessage(
      updateStatus({
        invoice_id: invoiceId,
        status,
      }).unwrap()
    ).catch(() => {});
  };

  const updateInvoiceVisibility = (
    invoiceId: number,
    isVisibleToSubUsers: boolean
  ) => {
    displayResultMessage(
      updateVisibility({
        invoice_id: invoiceId,
        is_visible_to_sub_users: isVisibleToSubUsers,
      }).unwrap()
    ).catch(() => undefined);
  };

  const getStatusTag = (invoice: AssetManagerInvoice) =>
    theme.isNewLook ? (
      <StatusDot
        color={
          getNewLookTokens(theme)[NEW_LOOK_STATUS_TO_TOKEN[invoice.status]]
        }
      >
        {INVOICE_STATUS_TO_DISPLAY[invoice.status]}
      </StatusDot>
    ) : (
      <StatusTag
        text={INVOICE_STATUS_TO_DISPLAY[invoice.status]}
        color={theme.colors[STATUS_TO_COLOR[invoice.status]]}
        withBgColor={true}
        style={{ width: "fit-content" }}
      />
    );

  const getInvoiceActions = (invoice: AssetManagerInvoice): InvoiceAction[] => {
    const fileName = invoice.pdf_url?.split("/").pop() || "";
    const actions: InvoiceAction[] = [
      {
        key: "download",
        icon: (
          <LookIcon
            classic={<DownloadOutlined />}
            newLook={<Download {...NEW_LOOK_MENU_ICON} />}
          />
        ),
        label: t("invoices.table.download"),
        onClick: () => downloadFile(invoice.pdf_url, fileName),
      },
    ];
    if (isSubUser) return actions;

    actions.push(
      {
        key: "visibility",
        icon: invoice.is_visible_to_sub_users ? (
          <LookIcon
            classic={<EyeInvisibleOutlined />}
            newLook={<EyeOff {...NEW_LOOK_MENU_ICON} />}
          />
        ) : (
          <LookIcon
            classic={<EyeOutlined />}
            newLook={<Eye {...NEW_LOOK_MENU_ICON} />}
          />
        ),
        label: t(
          invoice.is_visible_to_sub_users
            ? "invoices.visibility.hide_from_sub_users"
            : "invoices.visibility.show_to_sub_users"
        ),
        onClick: () =>
          updateInvoiceVisibility(invoice.id, !invoice.is_visible_to_sub_users),
      },
      {
        key: "delete",
        icon: (
          <LookIcon
            classic={<DeleteOutlined />}
            newLook={<Trash2 {...NEW_LOOK_MENU_ICON} />}
          />
        ),
        label: t("invoices.table.delete"),
        onClick: () =>
          updateInvoiceStatus(invoice.id, AssetManagerInvoiceStatus.DELETED),
      }
    );

    if (invoice.status === AssetManagerInvoiceStatus.PENDING) {
      actions.push(
        {
          key: "markAsPaid",
          icon: (
            <LookIcon
              classic={<CheckOutlined />}
              newLook={<Check {...NEW_LOOK_MENU_ICON} />}
            />
          ),
          label: t("invoices.table.mark_as_paid"),
          onClick: () =>
            updateInvoiceStatus(invoice.id, AssetManagerInvoiceStatus.PAID),
        },
        {
          key: "markAsCancelled",
          icon: (
            <LookIcon
              classic={<CloseOutlined />}
              newLook={<X {...NEW_LOOK_MENU_ICON} />}
            />
          ),
          label: t("invoices.table.mark_as_cancelled"),
          onClick: () =>
            updateInvoiceStatus(invoice.id, AssetManagerInvoiceStatus.CANCELED),
        }
      );
    } else if (invoice.status === AssetManagerInvoiceStatus.PAID) {
      actions.push({
        key: "markAsRefunded",
        icon: (
          <LookIcon
            classic={<SyncOutlined />}
            newLook={<RefreshCcw {...NEW_LOOK_MENU_ICON} />}
          />
        ),
        label: t("invoices.table.mark_as_refunded"),
        onClick: () =>
          updateInvoiceStatus(invoice.id, AssetManagerInvoiceStatus.REFUNDED),
      });
    }

    return actions;
  };

  return {
    getStatusTag,
    getInvoiceActions,
  };
};

export default useInvoice;

const NEW_LOOK_MENU_ICON = { size: 16, strokeWidth: 1.75 };

// New look: a quiet dot - amber while pending, green once paid, red when
// cancelled, grey for refunded or deleted.
const NEW_LOOK_STATUS_TO_TOKEN = {
  [AssetManagerInvoiceStatus.PENDING]: "stop",
  [AssetManagerInvoiceStatus.PAID]: "up",
  [AssetManagerInvoiceStatus.CANCELED]: "down",
  [AssetManagerInvoiceStatus.REFUNDED]: "muted",
  [AssetManagerInvoiceStatus.DELETED]: "muted",
} as const;

const STATUS_TO_COLOR = {
  [AssetManagerInvoiceStatus.PENDING]: "yellow",
  [AssetManagerInvoiceStatus.PAID]: "green",
  [AssetManagerInvoiceStatus.CANCELED]: "red",
  [AssetManagerInvoiceStatus.REFUNDED]: "secondaryGrey",
};
